import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ChevronRight, Folder, FolderOpen, Store as StoreIcon, Copy } from 'lucide-react';

const Categories = () => {
  const { token, selectedStore } = useAuth();
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyingCategory, setCopyingCategory] = useState(null);
  const [copyTargetStores, setCopyTargetStores] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [multiStoreMode, setMultiStoreMode] = useState(false);
  const [multiStoreSubcatMode, setMultiStoreSubcatMode] = useState(false);
  const [parentCategoryForSubcat, setParentCategoryForSubcat] = useState(null);
  const [selectedStores, setSelectedStores] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: null
  });

  useEffect(() => {
    fetchCategories();
    fetchStores();
  }, [selectedStore]);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Failed to load stores');
    }
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'Unknown Store';
  };

  const fetchCategories = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedStore) params.append('store_id', selectedStore);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Multi-store subcategory mode
    if (multiStoreSubcatMode && parentCategoryForSubcat && !editingCategory) {
      if (selectedStores.length === 0) {
        toast.error('Please select at least one store');
        return;
      }
      
      try {
        const payload = {
          name: formData.name,
          description: formData.description,
          parent_category_name: parentCategoryForSubcat.name,
          store_ids: selectedStores
        };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subcategories/bulk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(data.message);
          setDialogOpen(false);
          resetForm();
          fetchCategories();
        } else {
          const data = await response.json();
          toast.error(data.detail || 'Operation failed');
        }
      } catch (error) {
        toast.error('Network error');
      }
      return;
    }
    
    // Multi-store mode (creating main category in multiple stores)
    if (multiStoreMode && !editingCategory) {
      if (selectedStores.length === 0) {
        toast.error('Please select at least one store');
        return;
      }
      
      try {
        const payload = {
          name: formData.name,
          description: formData.description,
          parent_id: null,
          store_ids: selectedStores
        };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories/bulk`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(data.message);
          setDialogOpen(false);
          resetForm();
          fetchCategories();
        } else {
          const data = await response.json();
          toast.error(data.detail || 'Operation failed');
        }
      } catch (error) {
        toast.error('Network error');
      }
      return;
    }
    
    // Single store mode
    if (!selectedStore && !formData.parent_id) {
      toast.error('Please select a store first');
      return;
    }
    
    try {
      const url = editingCategory
        ? `${process.env.REACT_APP_BACKEND_URL}/api/categories/${editingCategory.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/categories`;

      const payload = { ...formData, store_id: selectedStore || parentCategoryForSubcat?.store_id };

      const response = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingCategory ? 'Category updated' : 'Category created');
        setDialogOpen(false);
        resetForm();
        fetchCategories();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Category deleted');
        fetchCategories();
        return;
      }

      const data = await response.json().catch(() => ({}));

      if (response.status === 400 && data.detail && /force delete/i.test(data.detail)) {
        const proceed = window.confirm(
          `${data.detail}\n\nFORCE DELETE: this will permanently delete this category AND every subcategory, product and variant inside it. This cannot be undone.\n\nContinue?`
        );
        if (!proceed) return;

        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories/${id}?force=true`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json().catch(() => ({}));
          const c = result.cascade;
          toast.success(
            c ? `Category deleted (removed ${c.subcategories} subcategories, ${c.products} products, ${c.variants} variants)` : 'Category deleted'
          );
          fetchCategories();
          return;
        }
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to force delete category');
        return;
      }

      toast.error(data.detail || 'Failed to delete category');
    } catch (error) {
      toast.error('Network error');
    }
  };

  const openCopyDialog = (category) => {
    setCopyingCategory(category);
    setCopyTargetStores([]);
    setCopyDialogOpen(true);
  };

  const handleCopyToStores = async () => {
    if (!copyingCategory || copyTargetStores.length === 0) {
      toast.error('Please select at least one store');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories/${copyingCategory.id}/copy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ store_ids: copyTargetStores })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setCopyDialogOpen(false);
        setCopyingCategory(null);
        setCopyTargetStores([]);
        fetchCategories();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to copy category');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const toggleCopyStoreSelection = (storeId) => {
    setCopyTargetStores(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', parent_id: null });
    setEditingCategory(null);
    setMultiStoreMode(false);
    setMultiStoreSubcatMode(false);
    setParentCategoryForSubcat(null);
    setSelectedStores([]);
  };

  const toggleStoreSelection = (storeId) => {
    setSelectedStores(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  };

  const selectAllStores = () => {
    if (selectedStores.length === stores.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(stores.map(s => s.id));
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setMultiStoreMode(false);
    setMultiStoreSubcatMode(false);
    setFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || null
    });
    setDialogOpen(true);
  };

  const openAddSubcategoryDialog = (parentCategory) => {
    resetForm();
    setParentCategoryForSubcat(parentCategory);
    setFormData({
      name: '',
      description: '',
      parent_id: parentCategory.id
    });
    setDialogOpen(true);
  };

  const toggleExpand = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getSubcategories = (parentId) => {
    return categories.filter(cat => cat.parent_id === parentId);
  };

  const getMainCategories = () => {
    return categories.filter(cat => !cat.parent_id);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'None';
  };

  const renderCategory = (category, level = 0) => {
    const subcategories = getSubcategories(category.id);
    const hasSubcategories = subcategories.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const showStoreName = !selectedStore; // Show store name when viewing all stores

    return (
      <div key={category.id} className="mb-2">
        <div
          className={`bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors duration-300 ${
            level > 0 ? 'ml-8' : ''
          }`}
          data-testid={`category-card-${category.id}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              {hasSubcategories && (
                <button
                  onClick={() => toggleExpand(category.id)}
                  className="mt-1 p-1 hover:bg-secondary rounded transition-colors"
                  data-testid={`toggle-expand-${category.id}`}
                >
                  <ChevronRight
                    size={18}
                    strokeWidth={1.5}
                    className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {isExpanded ? (
                    <FolderOpen size={18} strokeWidth={1.5} className="text-primary" />
                  ) : (
                    <Folder size={18} strokeWidth={1.5} className="text-muted-foreground" />
                  )}
                  <h3 className="font-heading font-bold text-lg">{category.name}</h3>
                  {hasSubcategories && (
                    <span className="font-mono text-xs bg-secondary px-2 py-0.5 rounded">
                      {subcategories.length} sub
                    </span>
                  )}
                  {showStoreName && category.store_id && (
                    <span className="font-mono text-xs bg-primary/20 text-primary px-2 py-0.5 rounded flex items-center gap-1">
                      <StoreIcon size={12} />
                      {getStoreName(category.store_id)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground font-body">
                  {category.description || 'No description'}
                </p>
                <p className="font-mono text-xs text-muted-foreground mt-2">
                  Created: {new Date(category.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex gap-1 ml-2">
              <button
                onClick={() => openCopyDialog(category)}
                className="p-2 hover:bg-secondary rounded transition-colors"
                title="Copy to other stores"
                data-testid={`copy-category-${category.id}`}
              >
                <Copy size={16} strokeWidth={1.5} className="text-primary" />
              </button>
              <button
                onClick={() => openAddSubcategoryDialog(category)}
                className="p-2 hover:bg-secondary rounded transition-colors"
                title="Add Subcategory"
                data-testid={`add-subcategory-${category.id}`}
              >
                <Plus size={16} strokeWidth={1.5} className="text-primary" />
              </button>
              <button
                onClick={() => openEditDialog(category)}
                className="p-2 hover:bg-secondary rounded transition-colors"
                data-testid={`edit-category-${category.id}`}
              >
                <Pencil size={16} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => handleDelete(category.id)}
                className="p-2 hover:bg-destructive/10 text-destructive rounded transition-colors"
                data-testid={`delete-category-${category.id}`}
              >
                <Trash2 size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
        {isExpanded && hasSubcategories && (
          <div className="mt-2">
            {subcategories.map(subcat => renderCategory(subcat, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="categories-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight" data-testid="categories-title">Categories</h1>
          <p className="text-muted-foreground font-body mt-2">Organize your inventory with categories and subcategories</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-category-btn">
              <Plus size={18} strokeWidth={1.5} className="mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="category-dialog" className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingCategory ? 'Edit Category' : formData.parent_id ? 'Add Subcategory' : 'Add New Category'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {formData.parent_id && parentCategoryForSubcat && (
                <div className="bg-secondary p-3 rounded-md border border-border">
                  <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Parent Category</p>
                  <p className="font-body font-medium">{parentCategoryForSubcat.name}</p>
                  {parentCategoryForSubcat.store_id && (
                    <p className="text-xs text-muted-foreground mt-1">Store: {getStoreName(parentCategoryForSubcat.store_id)}</p>
                  )}
                </div>
              )}

              {/* Multi-store toggle for subcategories */}
              {!editingCategory && formData.parent_id && parentCategoryForSubcat && stores.length > 1 && (
                <div className="bg-secondary/50 p-3 rounded-md border border-border">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={multiStoreSubcatMode}
                      onChange={(e) => {
                        setMultiStoreSubcatMode(e.target.checked);
                        if (!e.target.checked) setSelectedStores([]);
                      }}
                      className="w-4 h-4"
                      data-testid="multi-store-subcat-toggle"
                    />
                    <span className="font-mono text-sm font-medium">Add to multiple stores</span>
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">
                    Creates this subcategory under "{parentCategoryForSubcat.name}" in each selected store
                  </p>
                </div>
              )}

              {/* Multi-store selector for subcategories */}
              {multiStoreSubcatMode && !editingCategory && formData.parent_id && (
                <div className="border border-border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-mono text-xs uppercase tracking-widest">Select Stores</Label>
                    <button
                      type="button"
                      onClick={selectAllStores}
                      className="text-xs text-primary hover:underline"
                      data-testid="select-all-stores-subcat-btn"
                    >
                      {selectedStores.length === stores.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Only stores that have a "{parentCategoryForSubcat?.name}" category will get the subcategory
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {stores.map(store => (
                      <label 
                        key={store.id} 
                        className="flex items-center gap-2 p-2 hover:bg-secondary rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStores.includes(store.id)}
                          onChange={() => toggleStoreSelection(store.id)}
                          className="w-4 h-4"
                          data-testid={`subcat-store-checkbox-${store.id}`}
                        />
                        <StoreIcon size={14} className="text-muted-foreground" />
                        <span className="text-sm">{store.name}</span>
                      </label>
                    ))}
                  </div>
                  {selectedStores.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {selectedStores.length} store(s) selected
                    </p>
                  )}
                </div>
              )}

              {/* Multi-store toggle - only show when creating new main category */}
              {!editingCategory && !formData.parent_id && stores.length > 1 && (
                <div className="bg-secondary/50 p-3 rounded-md border border-border">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={multiStoreMode}
                      onChange={(e) => {
                        setMultiStoreMode(e.target.checked);
                        if (!e.target.checked) setSelectedStores([]);
                      }}
                      className="w-4 h-4"
                      data-testid="multi-store-toggle"
                    />
                    <span className="font-mono text-sm font-medium">Add to multiple stores</span>
                  </label>
                </div>
              )}

              {/* Multi-store selector for main categories */}
              {multiStoreMode && !editingCategory && !formData.parent_id && (
                <div className="border border-border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-mono text-xs uppercase tracking-widest">Select Stores</Label>
                    <button
                      type="button"
                      onClick={selectAllStores}
                      className="text-xs text-primary hover:underline"
                      data-testid="select-all-stores-btn"
                    >
                      {selectedStores.length === stores.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {stores.map(store => (
                      <label 
                        key={store.id} 
                        className="flex items-center gap-2 p-2 hover:bg-secondary rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStores.includes(store.id)}
                          onChange={() => toggleStoreSelection(store.id)}
                          className="w-4 h-4"
                          data-testid={`store-checkbox-${store.id}`}
                        />
                        <StoreIcon size={14} className="text-muted-foreground" />
                        <span className="text-sm">{store.name}</span>
                      </label>
                    ))}
                  </div>
                  {selectedStores.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {selectedStores.length} store(s) selected
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1.5"
                  data-testid="category-name-input"
                />
              </div>

              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                  data-testid="category-description-input"
                />
              </div>

              {!editingCategory && !formData.parent_id && !multiStoreMode && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Parent Category (Optional)</Label>
                  <select
                    value={formData.parent_id || ''}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                    data-testid="parent-category-select"
                  >
                    <option value="">None (Main Category)</option>
                    {getMainCategories().map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="category-submit-btn">
                  {editingCategory ? 'Update' : (multiStoreMode || multiStoreSubcatMode) ? `Create in ${selectedStores.length} Store(s)` : 'Create'} {formData.parent_id ? 'Subcategory' : 'Category'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => { setDialogOpen(false); resetForm(); }} data-testid="category-cancel-btn">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Categories List */}
      <div className="space-y-2" data-testid="categories-list">
        {loading ? (
          <div className="text-center p-8 text-muted-foreground font-mono">Loading...</div>
        ) : getMainCategories().length === 0 ? (
          <div className="text-center p-8 text-muted-foreground font-body">No categories found. Create your first category!</div>
        ) : (
          getMainCategories().map(category => renderCategory(category))
        )}
      </div>

      {/* Copy to Stores Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={(open) => { setCopyDialogOpen(open); if (!open) { setCopyingCategory(null); setCopyTargetStores([]); } }}>
        <DialogContent data-testid="copy-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl">
              Copy Category to Other Stores
            </DialogTitle>
          </DialogHeader>
          {copyingCategory && (
            <div className="space-y-4">
              <div className="bg-secondary p-3 rounded-md border border-border">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Copying Category</p>
                <p className="font-body font-medium">{copyingCategory.name}</p>
                {copyingCategory.store_id && (
                  <p className="text-xs text-muted-foreground mt-1">From: {getStoreName(copyingCategory.store_id)}</p>
                )}
                {/* Show subcategories that will be copied */}
                {!copyingCategory.parent_id && getSubcategories(copyingCategory.id).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs text-primary font-medium">Includes {getSubcategories(copyingCategory.id).length} subcategories:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {getSubcategories(copyingCategory.id).map(sub => (
                        <span key={sub.id} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">{sub.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-border rounded-md p-3">
                <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">Select Target Stores</Label>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {stores.filter(s => s.id !== copyingCategory.store_id).map(store => (
                    <label 
                      key={store.id} 
                      className="flex items-center gap-2 p-2 hover:bg-secondary rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={copyTargetStores.includes(store.id)}
                        onChange={() => toggleCopyStoreSelection(store.id)}
                        className="w-4 h-4"
                        data-testid={`copy-store-checkbox-${store.id}`}
                      />
                      <StoreIcon size={14} className="text-muted-foreground" />
                      <span className="text-sm">{store.name}</span>
                    </label>
                  ))}
                </div>
                {copyTargetStores.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {copyTargetStores.length} store(s) selected
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={handleCopyToStores} 
                  className="flex-1 bg-primary hover:bg-primary/90" 
                  data-testid="copy-category-submit-btn"
                  disabled={copyTargetStores.length === 0}
                >
                  Copy to {copyTargetStores.length} Store(s)
                </Button>
                <Button type="button" variant="secondary" onClick={() => { setCopyDialogOpen(false); setCopyingCategory(null); setCopyTargetStores([]); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Categories;
